import {useCallback} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {ItemUpdatesWithSourceRepoId, UpdateColumnValueAction} from '../../api/columns/contracts/storage'
import {isCopilot} from '../../helpers/assignee-helper'
import {useBulkUpdateItems} from '../../hooks/use-bulk-update-items'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {validateUpdate} from '../../hooks/use-update-item'
import type {MemexItemModel} from '../../models/memex-item-model'
import {BulkUpdateProgressSingleton} from '../../state-providers/column-values/bulk-update-progress'
import {useCopilotAssignmentModal} from './copilot-assignment-modal-provider'

// Helper to check if an update contains Copilot assignees
function hasCopilotAssignment(update: UpdateColumnValueAction): boolean {
  return (
    update.dataType === MemexColumnDataType.Assignees &&
    Array.isArray(update.value) &&
    update.value.some(assignee => isCopilot(assignee.login))
  )
}

// Hook for handling Copilot assignments in multi-value paste operations.
export const useCopilotMultiValueUpdate = (description: string) => {
  const {bulkUpdateMultipleColumnValues: bulkUpdateItems} = useBulkUpdateItems()
  const {openCopilotModalForIssues} = useCopilotAssignmentModal()
  const {issues_cca_assign_actor_with_agent} = useEnabledFeatures()

  return useCallback(
    async (itemUpdates: Array<ItemUpdatesWithSourceRepoId>, targetItems: Array<MemexItemModel>): Promise<boolean> => {
      // Check if any updates contain Copilot assignees
      const hasAssigneeUpdates = itemUpdates.some(itemUpdate =>
        itemUpdate.updates.some(update => hasCopilotAssignment(update)),
      )

      if (!hasAssigneeUpdates) {
        return false
      }

      const itemMap = new Map<number, MemexItemModel>()
      for (const item of targetItems) {
        itemMap.set(item.id, item)
      }

      // Separate updates into those with Copilot and those without
      const updatesWithCopilot: Array<ItemUpdatesWithSourceRepoId> = []
      const updatesWithoutCopilot: Array<ItemUpdatesWithSourceRepoId> = []
      const itemsWithCopilot: Array<MemexItemModel> = []
      let skippedUpdateCount = 0

      for (const itemUpdate of itemUpdates) {
        const item = itemMap.get(itemUpdate.itemId)
        if (!item) continue

        const hasCopilotInThisUpdate = itemUpdate.updates.some(update => hasCopilotAssignment(update))

        if (hasCopilotInThisUpdate) {
          // Validate updates (e.g., cross-repo assignments)
          try {
            for (const update of itemUpdate.updates) {
              if (update.dataType === MemexColumnDataType.Assignees) {
                validateUpdate([item], update, itemUpdate.sourceRepoId ?? null)
              }
            }

            // Split assignee updates with mixed Copilot/non-Copilot values
            const nonCopilotUpdates: Array<UpdateColumnValueAction> = []
            let hasCopilotUpdates = false

            for (const update of itemUpdate.updates) {
              if (update.dataType === MemexColumnDataType.Assignees && Array.isArray(update.value)) {
                const nonCopilotAssignees = update.value.filter(assignee => !isCopilot(assignee.login))
                const hasCopilotInValue = update.value.some(assignee => isCopilot(assignee.login))

                // Apply non-Copilot assignees immediately if present
                if (nonCopilotAssignees.length > 0) {
                  nonCopilotUpdates.push({...update, value: nonCopilotAssignees})
                }

                // Track if there are Copilot assignees
                if (hasCopilotInValue) {
                  hasCopilotUpdates = true
                }
              } else {
                // Non-assignee updates go to immediate updates
                nonCopilotUpdates.push(update)
              }
            }

            // Add non-Copilot parts to immediate updates
            if (nonCopilotUpdates.length > 0) {
              updatesWithoutCopilot.push({
                itemId: itemUpdate.itemId,
                updates: nonCopilotUpdates,
                sourceRepoId: itemUpdate.sourceRepoId,
              })
            }

            if (hasCopilotUpdates) {
              updatesWithCopilot.push(itemUpdate)
              itemsWithCopilot.push(item)
            }
          } catch {
            // Skip invalid items
            skippedUpdateCount++
            continue
          }
        } else {
          updatesWithoutCopilot.push(itemUpdate)
        }
      }

      // Apply updates without Copilot immediately
      if (updatesWithoutCopilot.length > 0) {
        await bulkUpdateItems(updatesWithoutCopilot, description)
      }

      // Open modal for items that have Copilot assignments
      if (updatesWithCopilot.length > 0) {
        const onSuccess = async () => {
          // Prevents duplicate assignment calls - when flag is enabled, skip update in callback as agent_assignment_controller already handles it
          if (!issues_cca_assign_actor_with_agent) {
            await bulkUpdateItems(updatesWithCopilot, description)
          }
          BulkUpdateProgressSingleton.skippedItemCount += skippedUpdateCount
        }

        return openCopilotModalForIssues(itemsWithCopilot, onSuccess)
      }

      return false
    },
    [bulkUpdateItems, openCopilotModalForIssues, description, issues_cca_assign_actor_with_agent],
  )
}
